import { createContext, useContext, useMemo } from "react";
import { ExplorerEnvironment } from "./Cache";
import { ExplorerAPI } from "../util/Requests";
import { getTeamLogoUrlForEnvironment } from "../models/Team";

interface LeagueEnvironmentContextValue {
  environment: ExplorerEnvironment;
  api: ExplorerAPI;
  getTeamLogoUrl: (nbaId: number) => string;
  getReportsBasePath: () => string;
  getReportsTeamPath: (teamAbbrev: string) => string;
  getPorTeamAbbrev: () => string;
}

const LeagueEnvironmentContext =
  createContext<LeagueEnvironmentContextValue | null>(null);

export interface LeagueEnvironmentProviderProps {
  environment: ExplorerEnvironment;
  children: React.ReactNode;
}

export function LeagueEnvironmentProvider({
  environment,
  children,
}: LeagueEnvironmentProviderProps) {
  const reportsBasePath =
    environment === ExplorerEnvironment.DRAFT
      ? "/reports/gleague"
      : "/reports/nba";
  const value = useMemo(
    () => ({
      environment,
      api: new ExplorerAPI(environment),
      getTeamLogoUrl: (nbaId: number) =>
        getTeamLogoUrlForEnvironment(nbaId, environment),
      getReportsBasePath: () => reportsBasePath,
      getReportsTeamPath: (teamAbbrev: string) =>
        `${reportsBasePath}/${teamAbbrev.toUpperCase()}`,
      getPorTeamAbbrev: () => environment === ExplorerEnvironment.NBA ? "POR" : "RCR",
    }),
    [environment, reportsBasePath]
  );

  return (
    <LeagueEnvironmentContext.Provider value={value}>
      {children}
    </LeagueEnvironmentContext.Provider>
  );
}

function useLeagueEnvironmentContext(): LeagueEnvironmentContextValue {
  const context = useContext(LeagueEnvironmentContext);
  if (!context) {
    throw new Error(
      "useLeagueEnvironment must be used within a LeagueEnvironmentProvider"
    );
  }
  return context;
}

export function useLeagueEnvironment(): ExplorerEnvironment {
  return useLeagueEnvironmentContext().environment;
}

export function useLeagueApi(): ExplorerAPI {
  return useLeagueEnvironmentContext().api;
}

export function useLeagueLogo(): (nbaId: number) => string {
  return useLeagueEnvironmentContext().getTeamLogoUrl;
}

export function useLeaguePaths(): Pick<
  LeagueEnvironmentContextValue,
  "getReportsBasePath" | "getReportsTeamPath" | "getPorTeamAbbrev"
> {
  const { getReportsBasePath, getReportsTeamPath, getPorTeamAbbrev } =
    useLeagueEnvironmentContext();
  return { getReportsBasePath, getReportsTeamPath, getPorTeamAbbrev };
}
